
"use client";

import { useEffect, useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import "./BlogEditor.css";

import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Link,
  List,
  BlockQuote,
  Code,
  CodeBlock,

  // Image plugins
  Image,
  ImageToolbar,
  ImageCaption,
  ImageStyle,
  ImageResize,
  ImageUpload,
  ImageInsert,
  ImageInsertViaUrl,
  ImageTextAlternative,
  Base64UploadAdapter,

  // Video plugin
  MediaEmbed,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

interface BlogEditorProps {
  editorId?: string;
  initialContent?: string;
  onChange: (content: string) => void;
  onReadyContent?: (getContent: () => string) => void;
}

export default function BlogEditor({
  editorId,
  initialContent = "",
  onChange,
  onReadyContent,
}: BlogEditorProps) {
  const editorRef = useRef<ClassicEditor | null>(null);
  const contentRef = useRef(initialContent);

  useEffect(() => {
    contentRef.current = initialContent;
  }, [initialContent]);

  return (
    <div id={editorId} className="w-full">
      <CKEditor
        editor={ClassicEditor}
        data={initialContent}
        config={{
          licenseKey: "GPL",

          plugins: [
            Essentials,
            Paragraph,
            Heading,
            Bold,
            Italic,
            Underline,
            Strikethrough,
            Link,
            List,
            BlockQuote,
            Code,
            CodeBlock,

            // Images
            Image,
            ImageToolbar,
            ImageCaption,
            ImageStyle,
            ImageResize,
            ImageUpload,
            ImageInsert,
            ImageInsertViaUrl,
            ImageTextAlternative,
            Base64UploadAdapter,

            // Videos
            MediaEmbed,
          ],

          toolbar: {
            items: [
              "undo",
              "redo",
              "|",
              "heading",
              "|",
              "bold",
              "italic",
              "underline",
              "strikethrough",
              "|",
              "link",
              "bulletedList",
              "numberedList",
              "|",
              "blockQuote",
              "code",
              "codeBlock",
              "|",
              "insertImage",
              "mediaEmbed",
            ],
            shouldNotGroupWhenFull: true,
          },

          image: {
            insert: {
              integrations: ["upload", "url"],
            },

            toolbar: [
              "imageTextAlternative",
              "toggleImageCaption",
              "|",
              "imageStyle:inline",
              "imageStyle:block",
              "imageStyle:side",
              "|",
              "resizeImage",
            ],

            resizeOptions: [
              {
                name: "resizeImage:original",
                value: null,
                label: "Original",
              },
              {
                name: "resizeImage:25",
                value: "25",
                label: "25%",
              },
              {
                name: "resizeImage:50",
                value: "50",
                label: "50%",
              },
              {
                name: "resizeImage:75",
                value: "75",
                label: "75%",
              },
            ],

            resizeUnit: "%",
          },

          mediaEmbed: {
            previewsInData: true,
          },

          placeholder: "Start writing your chapter...",
        }}
        onReady={(editor) => {
          editorRef.current = editor;

          onReadyContent?.(() => editor.getData());
        }}
        onChange={(_event, editor) => {
          const html = editor.getData();

          contentRef.current = html;
          onChange(html);
        }}
        onAfterDestroy={() => {
          editorRef.current = null;
        }}
      />

      <style>{`
  .ck-editor__editable_inline {
    min-height: 300px;
    max-height: 650px;
    overflow-y: auto;
  }

  .ck.ck-editor {
    width: 100%;
  }

  .ck.ck-toolbar {
    flex-wrap: wrap;
  }

  .ck-content img {
    max-width: 100%;
    height: auto;
  }

  .ck-content .media {
    max-width: 100%;
    margin: 20px auto;
  }
`}</style>
    </div>
  );
}
